import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ACTIVITY_OPERATIONS_QUERY_KEYS } from "../configs/queryKeys";
import { ActivityOperationsService } from "../services/activityOperations.service";
import type { ActivityOperationsFilters } from "../types/activityOperations.types";

export const useInfiniteActivityOperations = (
  filters: Omit<ActivityOperationsFilters, "page">,
  sortParams: string[] = [],
) => {
  return useInfiniteQuery({
    queryKey: ACTIVITY_OPERATIONS_QUERY_KEYS.list(filters, sortParams),
    queryFn: async ({ pageParam = 1 }) => {
      return ActivityOperationsService.getOperations({ ...filters, page: pageParam }, sortParams);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, pages } = lastPage.pagination;
      return page < pages ? page + 1 : undefined;
    },
    refetchOnWindowFocus: false,
  });
};

/**
 * Aggregated debit/credit/solde for the current filters. Only enabled when a
 * holder is selected — the balance is meaningful per employee/beneficiary.
 */
export const useActivityOperationsBalance = (filters: Omit<ActivityOperationsFilters, "page" | "per_page">) => {
  return useQuery({
    queryKey: ACTIVITY_OPERATIONS_QUERY_KEYS.balance(filters),
    queryFn: () => ActivityOperationsService.getBalance(filters),
    enabled: Boolean(filters.holder_id || filters.search),
    staleTime: 10 * 1000,
  });
};

export const useActivityModulesQuery = () => {
  return useQuery({
    queryKey: ACTIVITY_OPERATIONS_QUERY_KEYS.modules(),
    queryFn: () => ActivityOperationsService.getModules(),
    staleTime: 60 * 1000,
  });
};

export const useActivityReferencesQuery = (
  categorie?: string,
  familleId?: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ACTIVITY_OPERATIONS_QUERY_KEYS.references(categorie, familleId),
    queryFn: () => ActivityOperationsService.getReferences(categorie, familleId),
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true,
  });
};

/** Bons Plans : catégories (genres) présentes dans le module sélectionné. */
export const useActivityGenresQuery = (categorie?: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ACTIVITY_OPERATIONS_QUERY_KEYS.genres(categorie),
    queryFn: () => ActivityOperationsService.getGenres(categorie),
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true,
  });
};

/** Bons Plans : produits (familles), éventuellement filtrés par catégorie (genre). */
export const useActivityFamillesQuery = (
  categorie?: string,
  genreId?: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ACTIVITY_OPERATIONS_QUERY_KEYS.familles(categorie, genreId),
    queryFn: () => ActivityOperationsService.getFamilles(categorie, genreId),
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true,
  });
};

export const useInvoiceQuery = (invoiceId: string | null) => {
  return useQuery({
    queryKey: [...ACTIVITY_OPERATIONS_QUERY_KEYS.all, "invoice", invoiceId ?? ""],
    queryFn: () => ActivityOperationsService.getInvoice(invoiceId as string),
    enabled: Boolean(invoiceId),
  });
};

/** Rules of a policy, for the avoir/facture creation dialog. */
export const usePolicyRulesQuery = (policyId: string | null) => {
  return useQuery({
    queryKey: [...ACTIVITY_OPERATIONS_QUERY_KEYS.all, "policy-rules", policyId ?? ""],
    queryFn: () => ActivityOperationsService.getPolicyRules(policyId as string),
    enabled: Boolean(policyId),
    staleTime: 30 * 1000,
  });
};
